import { useEffect, useState } from "react"
import { useForm, Controller } from "react-hook-form"
import { Input, ToggleButtonGroup } from "../../../../../components"
import styles from "./meter-information.module.scss"
import { useSaveZambiaCreditInfoMeterNumberMutation } from "../../../../../../../service/approval-service"
import { toast } from "react-toastify"
import { useSelector } from "react-redux"
import { RootState } from "../../../../../../../stores/store"

type HasMeter = "yes" | "no"

interface MeterFormValues {
  meterNumber: string
}

const toggleOptions: { label: string; value: HasMeter }[] = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
]

interface MeterInformationProps {
  onCancel: () => void;
  onContinue: () => void;
}

export const MeterInformation = ({ onCancel, onContinue }: MeterInformationProps) => {
  const creditInfo = useSelector((state: RootState) => state.getSolar.creditInfo);

  const [saveZambiaCreditInfoMeterNumber, { isLoading: isSaving }] = useSaveZambiaCreditInfoMeterNumberMutation();

  const [hasMeter, setHasMeter] = useState<HasMeter | undefined>();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<MeterFormValues>({
    defaultValues: {
      meterNumber: "",
    },
  })

  useEffect(() => {
    try {
      if (creditInfo?.financialInformation?.meterNumber != null) {
        setHasMeter("yes");
        reset({
          meterNumber: creditInfo.financialInformation.meterNumber ?? "",
        });
      }
      else{
        setHasMeter("no");
      }
    } catch (error) {
      toast.error("Failed to fetch credit information")
    }
  }, [creditInfo, reset]);

  const handleHasMeterChange = (value: string) => {
    setHasMeter(value as HasMeter);
    reset({ meterNumber: "" });
  }

  const onSubmit = async (data: MeterFormValues) => {
    try {
      await saveZambiaCreditInfoMeterNumber({
        meterNumber: hasMeter === "yes" ? data.meterNumber : undefined,
      }).unwrap();
      onContinue();
    } catch (error) {
      toast.error("Failed to submit meter information");
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 className={styles.title}>Financial Information</h2>
          <p className={styles.subtitle}>Provide us with your meter details to verify your credit history.</p>
        </div>

        <p className={styles.questionLabel}>Do you have a meter currently?</p>

        <div className={styles.toggleGroupWrapper}>
          <ToggleButtonGroup
            options={toggleOptions}
            value={hasMeter}
            onChange={handleHasMeterChange}
            name="hasMeter"
            direction="horizontal"
          />
        </div>

        {hasMeter !== undefined && (
          <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit)}>
            {hasMeter === "yes" && (
              <>
                <p className={styles.inputLabel}>Enter Meter Number</p>
                <Controller
                  name="meterNumber"
                  control={control}
                  rules={{ required: "Meter number is required" }}
                  render={({ field }) => (
                    <Input
                      type="text"
                      placeholder='E.g "2413235245"'
                      value={field.value}
                      onChange={field.onChange}
                      variant="solid"
                      error={errors.meterNumber?.message}
                    />
                  )}
                />
              </>
            )}

            <div className={styles.footer}>
              <div className={styles.footerInner}>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={onCancel}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.continueBtn}
                  disabled={isSaving}
                >
                  Continue
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
